import React, {useCallback, useEffect, useState} from 'react'
import {Link, useNavigate} from 'react-router-dom'
import {toast} from 'react-toastify'
import {useAuth} from '../../../modules/auth'
import {shouldDisplayFonctionnalite} from '../../../../components/helpers'
import {LoadingData} from '../../../../components/LoadingData'
import {Pagination} from '../../../../components/Pagination'
import {
  ModeleSavDossierListe,
  SavFiltres,
  SavListeResponse,
} from '../../../../components/sav/Models'
import {getSavDossiers, savErreur} from '../../../../components/sav/requests'
import {
  formatSavDate,
  nomSavClient,
  nomSavUtilisateur,
  SAV_ORIGINES,
  SAV_STATUTS,
  SAV_STATUTS_FINAUX,
  urlSavDossier,
} from '../../../../components/sav/libelles'
import SavStatutBadge from '../components/SavStatutBadge'
import SavSourceIcone from '../components/SavSourceIcone'
import {useSavReferentiels} from '../components/useSavReferentiels'

const OUVERTS = Object.keys(SAV_STATUTS)
  .filter((c) => !SAV_STATUTS_FINAUX.includes(c as any))
  .join(',')

type Tuile = {
  titre: string
  sousTitre?: string
  fond: string
  valeur: (r: SavListeResponse) => string | number
  statut?: string
}

// Tuiles compteurs (maquette 1.3) ; un clic filtre la liste
const TUILES: Array<Tuile> = [
  {titre: 'Total SAV ouverts', fond: '#F1F1F4', statut: OUVERTS, valeur: (r) => somme(r, OUVERTS)},
  {
    titre: 'Nouveaux',
    sousTitre: 'À qualifier',
    fond: '#E9F3FF',
    statut: 'OUVERT',
    valeur: (r) => somme(r, 'OUVERT'),
  },
  {
    titre: 'Plus ancien',
    sousTitre: 'Délai max',
    fond: '#FFF8DD',
    valeur: (r) => age(r.plusAncienOuvert),
  },
  {
    titre: 'Retour attendu',
    sousTitre: 'Colis en route',
    fond: '#FFF8DD',
    statut: 'RETOUR_ATTENDU',
    valeur: (r) => somme(r, 'RETOUR_ATTENDU'),
  },
  {
    titre: 'En contrôle',
    sousTitre: 'Au labo',
    fond: '#E9F3FF',
    statut: 'RECU,EN_CONTROLE',
    valeur: (r) => somme(r, 'RECU,EN_CONTROLE'),
  },
  {
    titre: 'À solder',
    sousTitre: 'Avoir ou échange',
    fond: '#DFF6F8',
    statut: 'A_SOLDER,COMMANDE',
    valeur: (r) => somme(r, 'A_SOLDER,COMMANDE'),
  },
  {
    titre: 'Clôturés +',
    sousTitre: 'Sur 30 jours',
    fond: '#DFF6F8',
    statut: 'CLOS_ACCEPTE',
    valeur: (r) => r.compteurs.CLOS_ACCEPTE_30J ?? 0,
  },
]

function somme(reponse: SavListeResponse, statuts: string): number {
  return statuts.split(',').reduce((total, code) => total + (reponse.compteurs[code] ?? 0), 0)
}

function age(date: string | null): string {
  if (!date) return '—'
  const jours = Math.floor((Date.now() - new Date(date).getTime()) / 86400000)
  return `${jours} j`
}

const useDebounce = (valeur: string, delai = 400) => {
  const [stable, setStable] = useState(valeur)
  useEffect(() => {
    const t = setTimeout(() => setStable(valeur), delai)
    return () => clearTimeout(t)
  }, [valeur, delai])
  return stable
}

// Écran 1 : liste des SAV
const SavListPage = () => {
  const {currentUser} = useAuth()
  const navigate = useNavigate()
  const referentiels = useSavReferentiels()
  const [reponse, setReponse] = useState<SavListeResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [texte, setTexte] = useState('')
  const [reference, setReference] = useState('')
  const [filtres, setFiltres] = useState<SavFiltres>({})
  const [panneau, setPanneau] = useState(false)
  const q = useDebounce(texte)
  const ref = useDebounce(reference)
  const centres = referentiels?.centres ?? []

  const charger = useCallback(() => {
    setLoading(true)
    getSavDossiers({...filtres, q, reference: ref}, page)
      .then(setReponse)
      .catch((e) => toast.error(savErreur(e)))
      .finally(() => setLoading(false))
  }, [filtres, q, ref, page])

  useEffect(() => {
    charger()
  }, [charger])

  const filtrer = (changement: Partial<SavFiltres>) => {
    setPage(1)
    setFiltres((f) => ({...f, ...changement}))
  }

  const nbFiltres = Object.values(filtres).filter(
    (v) => v !== undefined && v !== '' && v !== false
  ).length

  return (
    <>
      <div className='card mb-5'>
        <div className='card-body d-flex flex-wrap gap-3 align-items-center'>
          <input
            className='form-control form-control-solid w-300px'
            placeholder='Rechercher un client, une immat., une pièce'
            value={texte}
            onChange={(e) => {
              setPage(1)
              setTexte(e.target.value)
            }}
          />
          <input
            className='form-control form-control-solid w-300px'
            placeholder='Rechercher par n° SAV, commande ou facture'
            value={reference}
            onChange={(e) => {
              setPage(1)
              setReference(e.target.value)
            }}
          />
          <button className='btn btn-light-primary' onClick={() => setPanneau(!panneau)}>
            <i className='bi bi-funnel'></i> Filtrer{' '}
            {nbFiltres > 0 && <span className='badge badge-primary ms-1'>{nbFiltres}</span>}
          </button>
          {nbFiltres > 0 && (
            <button
              className='btn btn-light'
              onClick={() =>
                filtrer({
                  statut: undefined,
                  centre: undefined,
                  origine: undefined,
                  source: undefined,
                  assigne: undefined,
                  retard: false,
                })
              }
            >
              Effacer les filtres
            </button>
          )}
          <div className='flex-grow-1'></div>
          {shouldDisplayFonctionnalite(currentUser, 'SAV_CONTROLER') && (
            <Link to='/sav/controle' className='btn btn-light-primary'>
              <i className='bi bi-tools'></i> Contrôle labo
            </Link>
          )}
          {shouldDisplayFonctionnalite(currentUser, 'SAV_RECEPTIONNER') && (
            <Link to='/sav/reception' className='btn btn-light-success'>
              <i className='bi bi-box-seam'></i> Réception quai
            </Link>
          )}
          {shouldDisplayFonctionnalite(currentUser, 'SAV_CREER') && (
            <Link to='/sav/nouveau' className='btn btn-success'>
              <i className='bi bi-plus-lg'></i> Ajouter un SAV
            </Link>
          )}
        </div>
        {panneau && (
          <div className='card-body border-top row g-4'>
            <div className='col-md-2'>
              <label className='form-label'>Statut</label>
              <select
                className='form-select'
                value={filtres.statut ?? ''}
                onChange={(e) => filtrer({statut: e.target.value || undefined})}
              >
                <option value=''>Tous</option>
                {Object.entries(SAV_STATUTS).map(([code, s]) => (
                  <option key={code} value={code}>
                    {s.libelle}
                  </option>
                ))}
              </select>
            </div>
            <div className='col-md-2'>
              <label className='form-label'>Centre</label>
              <select
                className='form-select'
                value={filtres.centre ?? ''}
                onChange={(e) => filtrer({centre: e.target.value || undefined})}
              >
                <option value=''>Tous</option>
                {centres.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.libelle}
                  </option>
                ))}
              </select>
            </div>
            <div className='col-md-2'>
              <label className='form-label'>Vente d'origine</label>
              <select
                className='form-select'
                value={filtres.origine ?? ''}
                onChange={(e) => filtrer({origine: e.target.value || undefined})}
              >
                <option value=''>Toutes</option>
                {Object.entries(SAV_ORIGINES).map(([code, libelle]) => (
                  <option key={code} value={code}>
                    {libelle}
                  </option>
                ))}
              </select>
            </div>
            <div className='col-md-2'>
              <label className='form-label'>Source</label>
              <select
                className='form-select'
                value={filtres.source ?? ''}
                onChange={(e) => filtrer({source: e.target.value || undefined})}
              >
                <option value=''>Toutes</option>
                {referentiels?.sources.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.libelle}
                  </option>
                ))}
              </select>
            </div>
            <div className='col-md-2'>
              <label className='form-label'>Assigné</label>
              <select
                className='form-select'
                value={filtres.assigne ?? ''}
                onChange={(e) => filtrer({assigne: e.target.value || undefined})}
              >
                <option value=''>Tous</option>
                <option value={currentUser?.id}>Moi</option>
              </select>
            </div>
            <div className='col-md-2 d-flex align-items-end'>
              <div className='form-check form-switch mb-3'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  id='savRetard'
                  checked={!!filtres.retard}
                  onChange={(e) => filtrer({retard: e.target.checked})}
                />
                <label className='form-check-label' htmlFor='savRetard'>
                  En retard seulement
                </label>
              </div>
            </div>
          </div>
        )}
      </div>

      {reponse && (
        <div className='row g-4 mb-5'>
          {TUILES.map((tuile) => {
            const active = tuile.statut && filtres.statut === tuile.statut
            return (
              <div className='col' key={tuile.titre}>
                <div
                  className={`rounded p-4 h-100 ${tuile.statut ? 'cursor-pointer' : ''} ${
                    active ? 'border border-2 border-primary' : ''
                  }`}
                  style={{backgroundColor: tuile.fond}}
                  onClick={() =>
                    tuile.statut && filtrer({statut: active ? undefined : tuile.statut})
                  }
                >
                  <div className='fs-2x fw-bolder'>{tuile.valeur(reponse)}</div>
                  <div className='fw-bold'>{tuile.titre}</div>
                  {tuile.sousTitre && <div className='text-muted fs-7'>{tuile.sousTitre}</div>}
                </div>
              </div>
            )
          })}
        </div>
      )}

      <div className='card'>
        <div className='card-body'>
          {loading && !reponse ? (
            <LoadingData />
          ) : (
            <>
              <div className='table-responsive'>
                <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-3'>
                  <thead>
                    <tr className='fw-bold text-muted'>
                      <th>Numéro</th>
                      <th>Assigné</th>
                      <th>Date</th>
                      <th>Statut</th>
                      <th>Canal</th>
                      <th>Client</th>
                      <th>Type client</th>
                      <th>Vente d'origine</th>
                      <th>Centre</th>
                      <th>Pièce(s)</th>
                      <th>Échéance</th>
                      <th className='text-end'>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reponse?.data.length ? (
                      reponse.data.map((d: ModeleSavDossierListe) => (
                        <tr key={d.id}>
                          <td>
                            <Link
                              to={urlSavDossier(d)}
                              className='fw-bolder text-gray-900 text-hover-success'
                            >
                              {d.numero}
                            </Link>
                          </td>
                          <td>{nomSavUtilisateur(d.utilisateurAssigne) || '—'}</td>
                          <td className='text-nowrap'>
                            <i className='bi bi-calendar3 me-1'></i>
                            {formatSavDate(d.dateCreation)}
                            <div className='text-muted fs-7'>
                              <i className='bi bi-clock me-1'></i>
                              {new Date(d.dateCreation).toLocaleTimeString('fr-FR', {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </div>
                          </td>
                          <td>
                            <SavStatutBadge code={d.statut.code} />
                          </td>
                          <td>
                            <SavSourceIcone source={d.source} />
                          </td>
                          <td>
                            <div className='fw-bold text-uppercase'>
                              {nomSavClient(d.client, d.contactNom)}
                            </div>
                            <div className='text-muted fs-7'>{d.client?.email}</div>
                          </td>
                          <td>
                            {d.client?.generique ? (
                              <span className='badge badge-light-warning'>Générique</span>
                            ) : (
                              <>
                                <span className='badge badge-light-success me-1'>CLIENT</span>
                                <span className='badge badge-light-primary'>
                                  {d.client?.professionnel ? 'Professionnel' : 'Particulier'}
                                </span>
                              </>
                            )}
                          </td>
                          <td>
                            {SAV_ORIGINES[d.origine]}
                            <div className='text-muted fs-7'>
                              {d.refCommandeExterne || d.refFactureExterne}
                            </div>
                          </td>
                          <td>{d.centre?.nom}</td>
                          <td>{d.lignes.map((l) => l.libellePiece).join(', ')}</td>
                          <td className={d.enRetard ? 'text-danger fw-bolder' : ''}>
                            {formatSavDate(d.dateEcheance)}
                          </td>
                          <td className='text-end text-nowrap'>
                            <button
                              className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
                              title='Ouvrir'
                              onClick={() => navigate(urlSavDossier(d))}
                            >
                              <i className='bi bi-eye fs-4'></i>
                            </button>
                            <button
                              className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm'
                              title='Modifier'
                              onClick={() => navigate(`/sav/dossiers/${d.id}/fiche`)}
                            >
                              <i className='bi bi-pencil-square fs-4'></i>
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={12} className='text-center text-muted'>
                          Aucun dossier SAV
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {reponse && reponse.pages > 1 && (
                <Pagination
                  totalPages={reponse.total}
                  nombreDePages={reponse.pages}
                  currentPage={page}
                  setCurrentPage={setPage}
                  locationState={0}
                />
              )}
            </>
          )}
        </div>
      </div>
    </>
  )
}

export default SavListPage
